<template>
  <TableRow :to="propertyRoute(property)">
    <TableCell>
      <PropertyPhotoPreview :image-url="property.coverImage" size="sm" />
      <TableCell stacked>
        <Text variant="label-medium">{{ title ?? property.displayName }}</Text>
        <Text variant="body-small" color="secondary">{{ additionalInfo }}</Text>
      </TableCell>
    </TableCell>

    <TableCell>
      <PropertyStatusLabel :status="property.status" small />
    </TableCell>

    <TableCell>
      <Text as="span" variant="body-medium" :color="property.leadsCount ? 'primary' : 'tertiary'">
        {{ property.leadsCount || '—' }}
      </Text>
    </TableCell>

    <TableCell v-if="showManager">
      <template v-if="property.manager">
        <Avatar :user-name="managerName" show-single-letter size="small" />
        <Text as="span" variant="body-medium">{{ managerLabel }}</Text>
      </template>
      <Text v-else as="span" variant="body-medium" color="tertiary">—</Text>
    </TableCell>

    <TableCell v-if="showPortfolio">
      <PortfolioChip
        v-if="property.portfolio"
        :portfolio="property.portfolio"
        @select="emit('select-portfolio', $event)"
      />
      <Text v-else as="span" variant="body-medium" color="tertiary">—</Text>
    </TableCell>

    <TableCell>
      <Text as="span" variant="body-medium" color="secondary" class="date">{{ dateInfo }}</Text>
    </TableCell>
  </TableRow>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import { Avatar, TableCell, TableRow, Text } from '@tallo/design-system';
import { teamMemberName } from '@tallo/investor/company';
import { useInvestorStore } from '@tallo/investor/investor';
import { PropertyPhotoPreview } from '@tallo/property';

import { PropertyListItemDto } from '../dtos/property-list-item.dto.interface';
import { propertyRoute } from '../utils/property-route.util';
import { propertyDateLabel, propertyManagerLabel, propertySummary } from '../utils/property-summary.util';
import PortfolioChip from './portfolio-chip.vue';
import PropertyStatusLabel from './property-status-label.vue';

const props = defineProps<{
  property: PropertyListItemDto;
  showPortfolio?: boolean;
  showManager?: boolean;
  /** In place of the property's name, e.g. its unit number inside its building */
  title?: string;
}>();
const emit = defineEmits<{ 'select-portfolio': [portfolioId: string] }>();

const { investor } = storeToRefs(useInvestorStore());

const additionalInfo = computed(() => propertySummary(props.property));

const managerName = computed(() => (props.property.manager ? teamMemberName(props.property.manager) : ''));
const managerLabel = computed(() => propertyManagerLabel(props.property, investor.value?.id));

const dateInfo = computed(() => propertyDateLabel(props.property));
</script>

<style scoped lang="scss">
// A date is short and reads as one thing; the wider columns give way before it wraps
.date {
  white-space: nowrap;
}
</style>
